import { Image, Pressable, View, type ViewStyle } from "react-native";
import { ChevronRight } from "lucide-react-native";
import { colors, typography } from "../theme/tokens";
import { MoonText } from "./MoonText";

/**
 * Avatar + name + subtitle row. Audit task 2.5.
 *
 * Canonicalises the CareTeamCard member row shape: 38px avatar circle
 * (image with initials fallback), name on top, role / clinic subtitle
 * underneath, optional trailing badge and right chevron.
 *
 * State coverage (Phase 1.0 matrix):
 *   - idle / pressed  — Pressable native handling
 *   - disabled        — opacity 0.5
 *   - error / focused / loading — display-only card; not applicable here.
 */
type Props = {
  name: string;
  /** Line beneath the name (role, clinic, etc.). */
  subtitle?: string;
  /** Remote avatar url. Falls back to initials when missing. */
  avatarUrl?: string | null;
  /** Right-side trailing element (badge, icon). */
  trailing?: React.ReactNode;
  onPress?: () => void;
  /** Show chevron on the right. Defaults to true when onPress is set. */
  chevron?: boolean;
  /** Hue used for the avatar initials background. Defaults to warm. */
  accentColor?: string;
  /** Top border for stacking inside a card. Defaults false (first row). */
  divider?: boolean;
  /** Override the divider color. Defaults to `colors.border`. Use a
   *  white-translucent value for glass-card stacks (CareTeamCard). */
  dividerColor?: string;
  disabled?: boolean;
  testID?: string;
  style?: ViewStyle;
};

const AVATAR_SIZE = 38;

function getInitials(name: string) {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  const first = parts[0];
  if (!first) return "?";
  const last = parts[parts.length - 1] ?? first;
  if (parts.length === 1) return first.charAt(0).toUpperCase();
  return (first.charAt(0) + last.charAt(0)).toUpperCase();
}

export function MoonAvatarNameCard({
  name,
  subtitle,
  avatarUrl,
  trailing,
  onPress,
  chevron,
  accentColor = colors.accent,
  divider,
  dividerColor,
  disabled,
  testID,
  style,
}: Props) {
  const showChevron = chevron ?? !!onPress;
  const Container = onPress ? Pressable : View;
  return (
    <Container
      accessibilityRole={onPress ? "button" : undefined}
      accessibilityLabel={name}
      onPress={onPress}
      disabled={disabled}
      testID={testID}
      style={[
        {
          flexDirection: "row",
          alignItems: "center",
          gap: 12,
          paddingHorizontal: 16,
          paddingVertical: 14,
          borderTopWidth: divider ? 1 : 0,
          borderTopColor: dividerColor ?? colors.border,
          opacity: disabled ? 0.5 : 1,
        },
        style,
      ]}
    >
      <View
        style={{
          width: AVATAR_SIZE,
          height: AVATAR_SIZE,
          borderRadius: AVATAR_SIZE / 2,
          backgroundColor: `${accentColor}1A`,
          alignItems: "center",
          justifyContent: "center",
          overflow: "hidden",
        }}
      >
        {avatarUrl ? (
          <Image
            source={{ uri: avatarUrl }}
            style={{ width: AVATAR_SIZE, height: AVATAR_SIZE }}
          />
        ) : (
          <MoonText
            style={{
              fontFamily: typography.families.sansMedium,
              fontSize: 14,
              fontWeight: "600",
              color: accentColor,
            }}
          >
            {getInitials(name)}
          </MoonText>
        )}
      </View>
      <View style={{ flex: 1, minWidth: 0 }}>
        <View style={{ flexDirection: "row", alignItems: "center", gap: 6 }}>
          <MoonText
            style={{
              fontFamily: typography.families.sansMedium,
              fontSize: 14,
              fontWeight: "500",
              color: colors.text,
              flexShrink: 1,
            }}
            numberOfLines={1}
          >
            {name}
          </MoonText>
          {trailing}
        </View>
        {subtitle ? (
          <MoonText
            variant="body-sm"
            color="textLight"
            style={{ marginTop: 1 }}
            numberOfLines={1}
          >
            {subtitle}
          </MoonText>
        ) : null}
      </View>
      {showChevron ? (
        <ChevronRight size={16} color={colors.textFaint} strokeWidth={1.5} />
      ) : null}
    </Container>
  );
}
